import React from 'react';

interface OptinodeAILogoProps {
  width?: number;
  height?: number;
  className?: string;
}

const OptinodeAILogo: React.FC<OptinodeAILogoProps> = ({ 
  width = 80, 
  height = 80, 
  className = '' 
}) => {
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 100 100" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Optinode AI Fan Palm Logo"
      style={{ display: 'block' }}
    >
      <defs>
        {/* Fan palm leaf gradient (natural green) */}
        <linearGradient id="fanLeafGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7CB342" stopOpacity="1" />
          <stop offset="50%" stopColor="#558B2F" stopOpacity="1" />
          <stop offset="100%" stopColor="#33691E" stopOpacity="1" />
        </linearGradient>
        
        {/* Alternate leaf gradient for variation */}
        <linearGradient id="fanLeafGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8BC34A" stopOpacity="1" />
          <stop offset="50%" stopColor="#689F38" stopOpacity="1" />
          <stop offset="100%" stopColor="#388E3C" stopOpacity="1" />
        </linearGradient>
        
        {/* Central hub gradient */}
        <radialGradient id="hubGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#A1887F" stopOpacity="1" />
          <stop offset="70%" stopColor="#8D6E63" stopOpacity="1" />
          <stop offset="100%" stopColor="#5D4037" stopOpacity="1" />
        </radialGradient>
        
        {/* Stem gradient */}
        <linearGradient id="stemGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#8D6E63" stopOpacity="1" />
          <stop offset="100%" stopColor="#5D4037" stopOpacity="1" />
        </linearGradient>
        
        {/* Subtle glow effect */}
        <filter id="fanGlow">
          <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Stem */}
      <path 
        d="M 50 95 L 50 82" 
        stroke="url(#stemGradient)" 
        strokeWidth="2.5" 
        strokeLinecap="round"
      />
      
      {/* Fan palm leaf segments - radiating from center */}
      <g filter="url(#fanGlow)">
        {/* Left side segments */}
        <path d="M 50 82 Q 25 75 8 65 Q 12 68 35 78 Q 48 81 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        <path d="M 50 82 Q 30 70 15 55 Q 18 58 38 72 Q 48 80 50 82" fill="url(#fanLeafGradient2)" opacity="0.9" />
        <path d="M 50 82 Q 35 65 22 45 Q 25 48 42 68 Q 49 79 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        <path d="M 50 82 Q 38 60 28 35 Q 31 38 45 63 Q 49 78 50 82" fill="url(#fanLeafGradient2)" opacity="0.9" />
        <path d="M 50 82 Q 42 55 35 25 Q 38 28 47 58 Q 49 77 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        <path d="M 50 82 Q 45 50 42 15 Q 45 18 48 53 Q 49 76 50 82" fill="url(#fanLeafGradient2)" opacity="0.9" />
        <path d="M 50 82 Q 47 45 47 8 Q 49 11 49 48 Q 50 75 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        
        {/* Center segments */}
        <path d="M 50 82 Q 48 45 48 5 Q 50 8 50 48 Q 50 75 50 82" fill="url(#fanLeafGradient2)" opacity="0.95" />
        <path d="M 50 82 Q 52 45 52 8 Q 51 11 51 48 Q 50 75 50 82" fill="url(#fanLeafGradient)" opacity="0.95" />
        
        {/* Right side segments */}
        <path d="M 50 82 Q 53 50 58 15 Q 55 18 52 53 Q 51 76 50 82" fill="url(#fanLeafGradient2)" opacity="0.9" />
        <path d="M 50 82 Q 58 55 65 25 Q 62 28 53 58 Q 51 77 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        <path d="M 50 82 Q 62 60 72 35 Q 69 38 55 63 Q 51 78 50 82" fill="url(#fanLeafGradient2)" opacity="0.9" />
        <path d="M 50 82 Q 65 65 78 45 Q 75 48 58 68 Q 51 79 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        <path d="M 50 82 Q 70 70 85 55 Q 82 58 62 72 Q 52 80 50 82" fill="url(#fanLeafGradient2)" opacity="0.9" />
        <path d="M 50 82 Q 75 75 92 65 Q 88 68 65 78 Q 52 81 50 82" fill="url(#fanLeafGradient)" opacity="0.9" />
        
        {/* Additional outer segments for fuller appearance */}
        <path d="M 50 82 Q 20 78 5 70 Q 8 72 30 80 Q 47 82 50 82" fill="url(#fanLeafGradient2)" opacity="0.8" />
        <path d="M 50 82 Q 25 72 12 60 Q 15 63 35 75 Q 48 81 50 82" fill="url(#fanLeafGradient)" opacity="0.8" />
        <path d="M 50 82 Q 32 68 20 50 Q 23 53 40 70 Q 49 80 50 82" fill="url(#fanLeafGradient2)" opacity="0.8" />
        <path d="M 50 82 Q 68 68 80 50 Q 77 53 60 70 Q 51 80 50 82" fill="url(#fanLeafGradient)" opacity="0.8" />
        <path d="M 50 82 Q 75 72 88 60 Q 85 63 65 75 Q 52 81 50 82" fill="url(#fanLeafGradient2)" opacity="0.8" />
        <path d="M 50 82 Q 80 78 95 70 Q 92 72 70 80 Q 53 82 50 82" fill="url(#fanLeafGradient)" opacity="0.8" />
        
        {/* Inner shorter segments */}
        <path d="M 50 82 Q 40 75 32 65 Q 35 67 45 77 Q 49 81 50 82" fill="url(#fanLeafGradient2)" opacity="0.85" />
        <path d="M 50 82 Q 44 72 40 58 Q 42 61 47 75 Q 49 80 50 82" fill="url(#fanLeafGradient)" opacity="0.85" />
        <path d="M 50 82 Q 56 72 60 58 Q 58 61 53 75 Q 51 80 50 82" fill="url(#fanLeafGradient2)" opacity="0.85" />
        <path d="M 50 82 Q 60 75 68 65 Q 65 67 55 77 Q 51 81 50 82" fill="url(#fanLeafGradient)" opacity="0.85" />
      </g>
      
      {/* Central connection hub */}
      <circle 
        cx="50" 
        cy="82" 
        r="3" 
        fill="url(#hubGradient)" 
        filter="url(#fanGlow)"
      />
      
      {/* Subtle animated light effects */}
      <g fill="#A5D6A7" opacity="0.4">
        <circle cx="35" cy="65" r="0.8">
          <animate attributeName="opacity" values="0.2;0.6;0.2" dur="4s" repeatCount="indefinite" />
        </circle>
        <circle cx="65" cy="65" r="0.8">
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="3.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="50" cy="45" r="1">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="4.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="42" cy="55" r="0.6">
          <animate attributeName="opacity" values="0.5;0.3;0.5" dur="3.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="58" cy="55" r="0.6">
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="3.2s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
};

export default OptinodeAILogo;